import { api } from "./client";
import { apiRoutes } from "util/apiRoutes";
import { resolveAiLocale } from "i18n/aiUi";

export type AiLocale = "en-US" | "ja-JP" | "ko-KR";

export interface AiSource {
  credibilityScore?: number | null;
  index?: number;
  publisher?: string | null;
  snippet?: string | null;
  supportsStance?: boolean | null;
  title?: string | null;
  url: string;
}

export interface AiMeta {
  awardCost: number;
  cached: boolean;
  capabilityVersion: string;
  invocationId: string;
  model: string;
  provider?: string;
  remainingAwards?: number | null;
}

export interface SuggestTeabatesRequest {
  issueId: number;
  locale?: AiLocale;
  maxSuggestions?: number;
  excludeExistingTeabateIds?: number[];
  excludePreviousClaims?: string[];
}

export interface SuggestedTeabateClaim {
  category: string;
  claim: string;
  contestednessBand: "broad_agreement" | "contested_but_legible" | "highly_contested";
  noveltyScore: number;
  reasoning: string;
  sources: AiSource[];
}

export interface SuggestTeabatesResponse {
  suggestions: SuggestedTeabateClaim[];
  meta: AiMeta;
}

export interface ArgumentEvaluationFallacy {
  confidence: number;
  explanation: string;
  type: string;
}

export interface ArgumentEvaluationSummary {
  clarity: number;
  evidenceSupport: number;
  fallacies: ArgumentEvaluationFallacy[];
  overallStrength: "weak" | "moderate" | "strong";
  suggestedRevision: string | null;
  termsNeedingDefinition: string[];
  weakPoints: string[];
}

export interface ArgumentCounterpoint {
  summary: string;
  sources: AiSource[];
}

export interface EvaluateArgumentRequest {
  claim?: string;
  draftArgument: string;
  issueId?: number;
  locale?: AiLocale;
  parentArgumentId?: number | null;
  stance: "agree" | "disagree";
  teabateId?: number;
}

export interface EvaluateArgumentResponse {
  counterpoints: ArgumentCounterpoint[];
  evaluation: ArgumentEvaluationSummary;
  meta: AiMeta;
  sources: AiSource[];
}

export interface SubmitAiFeedbackRequest {
  invocationId: string;
  rating: -1 | 0 | 1;
  reason?: string;
  suggestionKey?: string;
}

export type GraphAnalysisMissingLinkKind = "bridge" | "downstream" | "upstream" | "lever";
export type GraphAnalysisInsightKind =
  | "cluster"
  | "feedback_loop"
  | "chain"
  | "isolate"
  | "bridge_gap";
export type GraphAnalysisDirection = "influences" | "influenced_by";
export type GraphAnalysisConfidence = "low" | "medium" | "high";

export interface GraphAnalysisStructuralInsight {
  kind: GraphAnalysisInsightKind;
  title: string;
  description: string;
  nodeIds: number[];
  edgeIds?: number[] | null;
}

export interface GraphAnalysisRootCauseCandidate {
  nodeId: number;
  nodeName: string;
  confidence: GraphAnalysisConfidence;
  rationale: string;
  evidence: AiSource[];
}

export interface GraphAnalysisMissingLink {
  suggestionKey: string;
  kind: GraphAnalysisMissingLinkKind;
  wikipediaTitle: string;
  wikipediaUrl: string;
  wikidataQid: string | null;
  suggestedDescription: string | null;
  anchorNodeId: number;
  anchorNodeName: string | null;
  direction: GraphAnalysisDirection;
  rationale: string;
  sources: AiSource[];
}

export interface GraphAnalysisRunStats {
  nodeCount: number;
  edgeCount: number;
  droppedNodes: number;
  droppedEdges: number;
  peripheralCandidateCount: number;
  droppedSuggestions: Record<string, number>;
}

export interface GraphAnalysisMeta extends AiMeta {
  locale: AiLocale;
  truncated?: {
    nodes: boolean;
    edges: boolean;
  };
}

export interface AnalyzeGraphRequest {
  collectionId: number;
  focusNodeIds?: number[];
  locale?: AiLocale;
}

export interface AnalyzeGraphResponse {
  overallSummary: string;
  structuralInsights: GraphAnalysisStructuralInsight[];
  rootCauseCandidates: GraphAnalysisRootCauseCandidate[];
  missingLinks: GraphAnalysisMissingLink[];
  analysis: GraphAnalysisRunStats;
  meta: GraphAnalysisMeta;
}

export interface AcceptGraphSuggestionRequest {
  collectionId: number;
  invocationId: string;
  suggestionKey: string;
}

export interface AcceptGraphSuggestionResponse {
  issueId: number;
  compoundIssueId: number;
  reused: boolean;
  suggestionKey: string;
  relation: Record<string, unknown>;
}

/**
 * Apply the locale fallback after spread so callers that omit `locale`
 * (or pass it as `undefined`) still get the user's resolved locale on
 * the request, matching what the rest of the AI UI assumes.
 */
function withResolvedLocale<T extends { locale?: AiLocale }>(payload: T): T {
  return {
    ...payload,
    locale: payload.locale ?? resolveAiLocale(),
  };
}

export const aiApi = {
  async suggestTeabates(payload: SuggestTeabatesRequest): Promise<SuggestTeabatesResponse> {
    const response = await api.post<SuggestTeabatesResponse>(
      apiRoutes.ai.teabateSuggestions,
      withResolvedLocale(payload)
    );
    return response.data;
  },

  async evaluateArgument(payload: EvaluateArgumentRequest): Promise<EvaluateArgumentResponse> {
    const response = await api.post<EvaluateArgumentResponse>(
      apiRoutes.ai.evaluateArgument,
      withResolvedLocale(payload)
    );
    return response.data;
  },

  async submitFeedback(payload: SubmitAiFeedbackRequest): Promise<void> {
    await api.post(apiRoutes.ai.feedback, payload);
  },

  async analyzeGraph(payload: AnalyzeGraphRequest): Promise<AnalyzeGraphResponse> {
    const response = await api.post<AnalyzeGraphResponse>(
      apiRoutes.ai.graph.analyze,
      withResolvedLocale(payload)
    );
    return response.data;
  },

  async acceptGraphSuggestion(
    payload: AcceptGraphSuggestionRequest
  ): Promise<AcceptGraphSuggestionResponse> {
    const response = await api.post<AcceptGraphSuggestionResponse>(
      apiRoutes.ai.graph.acceptSuggestion,
      payload
    );
    return response.data;
  },
};
